import type { BackendKind } from "@/api/types";

export interface CredentialField {
  key: string;
  label: string;
  placeholder?: string;
  description?: string;
  /** Renders as a password input. */
  secret?: boolean;
  /** Renders as a textarea (for connection strings). */
  multiline?: boolean;
  /** Belongs to a named auth group; user fills one group or the other. */
  group?: string;
}

export interface KindMeta {
  label: string;
  description: string;
  docsUrl?: string;
  fields: CredentialField[];
  /** If two or more groups exist, the form renders a tab strip. */
  groups?: { id: string; label: string; description?: string }[];
}

export const BACKEND_KIND_META: Record<BackendKind, KindMeta> = {
  grafana: {
    label: "Grafana / LGTM",
    description:
      "Push logs to Loki, metrics to Mimir, traces to Tempo. Use the URLs of your stack.",
    fields: [
      {
        key: "loki_url",
        label: "Loki URL",
        placeholder: "https://logs-prod-us-central1.grafana.net",
      },
      {
        key: "mimir_url",
        label: "Mimir URL",
        placeholder: "https://prometheus-prod-13-prod-us-east-0.grafana.net/api/prom",
      },
      {
        key: "tempo_url",
        label: "Tempo URL",
        placeholder: "https://tempo-prod-04-prod-us-east-0.grafana.net/tempo",
      },
      {
        key: "auth_token",
        label: "Bearer token",
        description: "Optional. Sent as Authorization: Bearer <token> to all three.",
        secret: true,
      },
    ],
  },
  azure: {
    label: "Azure Application Insights",
    description:
      "Use the App Insights connection string (preferred) or the legacy DCR path with service-principal auth.",
    docsUrl:
      "https://learn.microsoft.com/en-us/azure/azure-monitor/app/sdk-connection-string",
    groups: [
      {
        id: "conn",
        label: "Connection string",
        description: "Simplest path — uses the ingestion key embedded in the string.",
      },
      {
        id: "dcr",
        label: "DCR + service principal",
        description: "Legacy data-collection-rule path. Requires all five fields.",
      },
    ],
    fields: [
      {
        group: "conn",
        key: "AZURE_APPINSIGHTS_CONNECTION_STRING",
        label: "Connection string",
        placeholder:
          "InstrumentationKey=…;IngestionEndpoint=https://…;LiveEndpoint=https://…",
        secret: true,
        multiline: true,
      },
      {
        group: "dcr",
        key: "AZURE_TENANT_ID",
        label: "Tenant ID",
        placeholder: "00000000-0000-0000-0000-000000000000",
      },
      {
        group: "dcr",
        key: "AZURE_CLIENT_ID",
        label: "Client ID",
        placeholder: "00000000-0000-0000-0000-000000000000",
      },
      {
        group: "dcr",
        key: "AZURE_CLIENT_SECRET",
        label: "Client secret",
        secret: true,
      },
      {
        group: "dcr",
        key: "AZURE_DCE_ENDPOINT",
        label: "DCE endpoint",
        placeholder: "https://my-dce.eastus-1.ingest.monitor.azure.com",
      },
      {
        group: "dcr",
        key: "AZURE_DCR_RULE_ID",
        label: "DCR immutable rule ID",
        placeholder: "dcr-…",
      },
    ],
  },
  datadog: {
    label: "Datadog",
    description: "Push logs, metrics, and traces via the public intake APIs.",
    docsUrl: "https://docs.datadoghq.com/api/latest/authentication/",
    fields: [
      { key: "DATADOG_API_KEY", label: "API key", secret: true },
      {
        key: "DATADOG_APP_KEY",
        label: "Application key",
        description: "Needed for metrics queries; logs/traces only need the API key.",
        secret: true,
      },
      {
        key: "DATADOG_ENDPOINT",
        label: "Site",
        placeholder: "datadoghq.com",
        description: "datadoghq.com (US1) · datadoghq.eu · us3.datadoghq.com · …",
      },
    ],
  },
  dynatrace: {
    label: "Dynatrace",
    description:
      "OTLP intake. Use a Dynatrace API token (preferred) or an OAuth client.",
    docsUrl:
      "https://docs.dynatrace.com/docs/dynatrace-api/basics/dynatrace-api-authentication",
    groups: [
      {
        id: "token",
        label: "API token",
        description: "Token with metrics.ingest, logs.ingest, openTelemetryTrace.ingest scopes.",
      },
      {
        id: "oauth",
        label: "OAuth client",
        description: "If your tenant requires OAuth, set the client ID + secret instead.",
      },
    ],
    fields: [
      {
        key: "DYNATRACE_ENDPOINT",
        label: "Endpoint",
        placeholder: "https://abc12345.live.dynatrace.com",
      },
      { group: "token", key: "DYNATRACE_API_TOKEN", label: "API token", secret: true },
      { group: "oauth", key: "DYNATRACE_CLIENT_ID", label: "Client ID" },
      { group: "oauth", key: "DYNATRACE_CLIENT_SECRET", label: "Client secret", secret: true },
    ],
  },
  aws: {
    label: "AWS (CloudWatch + X-Ray)",
    description:
      "Uses the standard boto3 credential chain. Leave blank to fall back to ~/.aws/credentials on the runner host.",
    docsUrl: "https://docs.aws.amazon.com/cli/latest/userguide/cli-configure-files.html",
    fields: [
      { key: "AWS_ACCESS_KEY_ID", label: "Access key ID" },
      { key: "AWS_SECRET_ACCESS_KEY", label: "Secret access key", secret: true },
      {
        key: "AWS_SESSION_TOKEN",
        label: "Session token",
        description: "Only required when using temporary STS credentials.",
        secret: true,
      },
      { key: "AWS_REGION", label: "Region", placeholder: "us-east-1" },
    ],
  },
  splunk: {
    label: "Splunk (HEC)",
    description:
      "Push logs, metrics, and traces as JSON events via HTTP Event Collector. Verify queries use `| spath` to extract nested `event.*` fields — no sourcetype `KV_MODE=json` config required on the index.",
    docsUrl:
      "https://docs.splunk.com/Documentation/Splunk/latest/Data/UsetheHTTPEventCollector",
    fields: [
      {
        key: "SPLUNK_HEC_URL",
        label: "HEC base URL",
        placeholder: "http://splunk.example.com:8088",
        description:
          "Base URL only — the runner appends /services/collector/event. Pasting the full event URL is fine; the trailing path is stripped.",
      },
      {
        key: "SPLUNK_HEC_TOKEN",
        label: "HEC token",
        secret: true,
        description:
          "Create under Settings > Data Inputs > HTTP Event Collector.",
      },
      {
        key: "SPLUNK_INDEX",
        label: "Index",
        placeholder: "(leave blank to use the token's default)",
        description:
          "Optional. Leave blank to let Splunk route to the HEC token's default index — most tokens are pinned to a fixed index at creation and reject an explicit override with HTTP 400 \"Incorrect index\". Set this only if your token allows the SE to choose.",
      },
    ],
  },
  adx: {
    label: "Azure Data Explorer",
    description:
      "Ingest logs, metrics, and traces directly into the DemoSimLogs / DemoSimMetrics / DemoSimTraces Kusto tables. Auth uses an Azure service principal with Database Ingestor on the target database.",
    docsUrl:
      "https://learn.microsoft.com/en-us/azure/data-explorer/kusto/api/connection-strings/kusto",
    fields: [
      {
        key: "ADX_CLUSTER_URI",
        label: "Cluster URI",
        placeholder: "https://mycluster.eastus.kusto.windows.net",
        description:
          "Query endpoint. The runner derives the ingestion URI (ingest-<cluster>...) from this. Provide the ingestion URI below instead if your cluster uses a non-standard ingestion host.",
      },
      {
        key: "ADX_INGEST_URI",
        label: "Ingestion URI",
        placeholder: "(optional — https://ingest-mycluster.eastus.kusto.windows.net)",
        description:
          "Optional. Only needed when it can't be derived from the cluster URI.",
      },
      {
        key: "ADX_DATABASE",
        label: "Database",
        placeholder: "demosim",
        description: "Target Kusto database holding the DemoSim* tables.",
      },
      {
        key: "AZURE_TENANT_ID",
        label: "Tenant ID",
        description: "Azure AD tenant of the ingesting service principal.",
      },
      {
        key: "AZURE_CLIENT_ID",
        label: "Client ID",
        description: "Service-principal (app registration) client ID.",
      },
      {
        key: "AZURE_CLIENT_SECRET",
        label: "Client secret",
        secret: true,
        description: "Service-principal secret. Needs Database Ingestor on the database.",
      },
    ],
  },
  solarwinds: {
    label: "SolarWinds Platform (Orion)",
    description:
      "On-prem Orion, over SWIS only (port 17774). Creates one status-only node per service and writes its backdated response-time, CPU/memory and event history into the Orion DB through Orion.Reporting.ExecuteSQL — no SQL login or port 1433. Unsupported by SolarWinds — demo tenants only, never a customer's production Orion. Traces are skipped.",
    docsUrl: "https://github.com/solarwinds/OrionSDK/wiki/REST",
    fields: [
      {
        key: "SOLARWINDS_SWIS_URL",
        label: "SWIS URL",
        placeholder: "https://orion.example.com:17774",
        description: "Any path after the port is stripped.",
      },
      {
        key: "SOLARWINDS_USERNAME",
        label: "Orion username",
        description: "Orion admin — ExecuteSQL needs it.",
      },
      { key: "SOLARWINDS_PASSWORD", label: "Orion password", secret: true },
      {
        key: "SOLARWINDS_VERIFY_TLS",
        label: "Verify TLS",
        placeholder: "false",
        description:
          "Defaults to false — Orion ships a self-signed certificate on 17774. Set to true once a real certificate is installed.",
      },
      {
        key: "SOLARWINDS_ENGINE_ID",
        label: "Polling engine ID",
        placeholder: "(primary engine)",
        description: "Engine that owns the created nodes.",
      },
    ],
  },
};

/**
 * Raven webhook routing fields. Common to every backend kind — each
 * backend can target its own NeuBird webhook connection so alerts don't
 * cross projects when the SE swaps backends mid-demo.
 *
 * The runner POSTs to `{raven_url}/api/v1/event` with `Authorization:
 * Bearer {raven_ingest_token}` and `X-Hub-Signature-256` signed with
 * `raven_hmac_secret`. Tokens are pre-provisioned per-connection in
 * `public.connection.info.ingest_token` on the Raven side.
 */
export const RAVEN_FIELDS: CredentialField[] = [
  {
    key: "raven_url",
    label: "Raven URL",
    placeholder: "https://demosim.app.neubird.ai",
    description: "Base URL of the NeuBird Raven instance receiving the alert webhook.",
  },
  {
    key: "raven_ingest_token",
    label: "Ingest token",
    secret: true,
    description:
      "Per-connection bearer token from public.connection.info.ingest_token. Each backend's webhook connection has its own token.",
  },
  {
    key: "raven_hmac_secret",
    label: "HMAC secret",
    secret: true,
    description: 'HMAC-SHA256 signing secret. Leave blank to use the tenant default ("demosim").',
  },
];

/**
 * Decide which auth group is active based on which fields the existing
 * credentials populate. Defaults to the first declared group.
 */
export function detectActiveGroup(
  meta: KindMeta,
  credentials: Record<string, string>,
): string | null {
  if (!meta.groups || meta.groups.length === 0) return null;
  for (const g of meta.groups) {
    const fields = meta.fields.filter((f) => f.group === g.id);
    if (fields.some((f) => credentials[f.key])) return g.id;
  }
  return meta.groups[0].id;
}
